import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Modal, ModalCloseButton, ModalOverlay, ModalPortal, ModalTitle } from '../../../../design-system/modal/modal';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { format, isSameYear, UTCDate } from '../../../../utils/dates';

type DeleteBeneficiaryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  beneficiaryName: string;
  lastPaidDate?: Date;
  isLoading?: boolean;
};

export const DeleteBeneficiaryModal = ({
  isOpen,
  onClose,
  onConfirm,
  beneficiaryName,
  lastPaidDate,
  isLoading = false,
}: DeleteBeneficiaryModalProps) => {
  const formattedLastPaidDate = lastPaidDate
    ? format(lastPaidDate, isSameYear(new UTCDate(), lastPaidDate) ? 'dd MMM' : 'dd MMM yyyy')
    : null;
  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) {
      onClose();
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={handleOpenChange}>
      <ModalPortal>
        <ModalOverlay className="bg-black/50" />

        <DialogPrimitive.Content
          className={cn(
            'fixed top-[50%] left-[50%] z-50 grid w-[425px] max-w-[calc(100vw-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border border-border bg-white p-6 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
          aria-describedby={undefined}
        >
          <ModalCloseButton onClick={onClose} disabled={isLoading} />

          <Stack space={6} align="stretch">
            <ModalTitle className="text-lg leading-none font-semibold text-foreground">
              Delete {beneficiaryName}?
            </ModalTitle>

            <Stack space={2}>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <span className="font-medium">{beneficiaryName}</span>?
              </p>
              {formattedLastPaidDate && (
                <p className="text-sm text-muted-foreground">
                  Last paid on <span className="font-medium">{formattedLastPaidDate}</span>.
                </p>
              )}
              <p className="text-sm text-muted-foreground">You'll need to add them again to make future payments.</p>
            </Stack>

            <Inline space={2} justify="end" className="w-full">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>

              <Button type="button" variant="destructive" onClick={onConfirm} isLoading={isLoading}>
                Delete
              </Button>
            </Inline>
          </Stack>
        </DialogPrimitive.Content>
      </ModalPortal>
    </Modal>
  );
};
